import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { getMarketplaceStoreRules, getMarketplaceFeedHealth } from '@/api';
import MarketplaceFeedRules from './MarketplaceFeedRules';
import { normalizeStoreRules, hasMarketplaceConfig, apiErrorMessage } from './constants';

/**
 * The Rules tab covers two unrelated pipelines that happen to share a name.
 *
 *   Marketplace (FTP → S3 → CDC): a "rule" is the store's column mapping plus
 *     its transform steps, held in the CDC Postgres. This is where Glue imports
 *     land, so it wins the tab whenever the store has any CDC config.
 *   Direct / POS (Clover et al.): a "rule" is a price adjustment, held in Mongo.
 *
 * A store is realistically only ever one of the two, so rather than guess from
 * theme or provider flags we ask the CDC API what it holds for this store and
 * render whichever surface actually has something to show.
 */
const Rules = ({ hasPosConnection }) => {
  const storeId = useSelector((state) => state.app.selectedLocation?.store_id);

  const [status, setStatus] = useState('loading'); // loading | ready | unavailable | error
  const [store, setStore] = useState(null);
  const [stepsSupported, setStepsSupported] = useState(true);
  const [sourcesSupported, setSourcesSupported] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!storeId) {
        setStatus('ready');
        setStore(null);
        return;
      }
      if (!quiet) setStatus('loading');

      try {
        const res = await getMarketplaceStoreRules(storeId);
        setStore(normalizeStoreRules(res.store));
        setStatus('ready');
        setError(null);
      } catch (err) {
        // 503 means CDC_POSTGRES_URL is unset on the API, not that the store is
        // misconfigured — fall through to the POS surface rather than showing a
        // scary error on a direct seller's page.
        if (err?.response?.status === 503) {
          setStatus('unavailable');
          setError(apiErrorMessage(err, 'Marketplace feed datastore is not configured'));
        } else {
          console.error('Failed to load marketplace feed rules:', err);
          setStatus('error');
          setError(apiErrorMessage(err, 'Failed to load marketplace feed rules'));
        }
        setStore(null);
      }

      try {
        const health = await getMarketplaceFeedHealth();
        setStepsSupported(health?.health?.steps_supported !== false);
        // Without this, an un-migrated transform_sources reads as "every store
        // is single-file" rather than "not migrated" — the manifest would just
        // look empty.
        setSourcesSupported(health?.health?.sources_supported !== false);
      } catch {
        // Non-fatal: the store view already explains an un-migrated database in
        // its execution-path reason.
        setStepsSupported(true);
        setSourcesSupported(true);
      }
    },
    [storeId]
  );

  useEffect(() => {
    load();
  }, [load]);

  if (status === 'loading') {
    return (
      <div className="py-12 text-center text-slate-500 dark:text-slate-400">Loading rules…</div>
    );
  }

  if (status === 'error') {
    return (
      <div className="max-w-xl mx-auto bg-white dark:bg-theme-surface-accent border border-rose-200 dark:border-rose-800 rounded-lg p-6 text-center">
        <p className="text-rose-700 dark:text-rose-300">{error}</p>
        <button
          onClick={() => load()}
          className="mt-4 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // The CDC view wins when the store has any config, and also when it has none
  // but there is no POS either — for a marketplace store that just means the
  // pipeline has not resolved it yet, and its empty state beats a dead end.
  if (status === 'ready' && store && (hasMarketplaceConfig(store) || !hasPosConnection)) {
    return (
      <MarketplaceFeedRules
        store={store}
        stepsSupported={stepsSupported}
        sourcesSupported={sourcesSupported}
        onRefresh={() => load({ quiet: true })}
      />
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white dark:bg-theme-surface-accent border border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center">
      <p className="text-slate-600 dark:text-slate-400">
        {status === 'unavailable'
          ? error
          : 'No feed rules for this store. Connect a POS system to configure price adjustments.'}
      </p>
      {status !== 'unavailable' && (
        <Link
          to="/external-feeds/pos"
          className="btn bg-indigo-500 hover:bg-indigo-600 text-white mt-4"
        >
          Go to POS Connection
        </Link>
      )}
    </div>
  );
};

export default Rules;
